import {
  Directive,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  Input,
  NgZone,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  Renderer2,
  HostListener,
} from '@angular/core';

@Directive({
  selector: '[appProgressFlowLines]',
})
export class ProgressFlowLinesDirective implements AfterViewInit, OnDestroy, OnChanges {
  // INPUTS PARA TREE LINES (LINHAS DE HIERARQUIA)
  @Input() lineStyle: 'dashed' | 'solid' | 'dotted' = 'dashed';
  @Input() treeLineWidth: number = 1.5;

  // INPUTS PARA RETURN LINES (LINHAS DE RETORNO)
  @Input() returnLineWidth: number = 2;
  @Input() arrowSize: number = 8;
  @Input() isChoosingReturn: boolean = false;
  @Input() selectedSourceEtapa: any = null;
  @Input() selectedTargetEtapa: any = null;

  @Output() expandTargetEtapa = new EventEmitter<{ targetId: number; parentId: number }>();

  private treeSvgElement: SVGElement; // SVG para linhas de hierarquia
  private returnSvgElement: SVGElement; // SVG para linhas de retorno
  private returnDefsElement: SVGDefsElement; // Defs para marcadores de seta

  private mutationObserver: MutationObserver;
  private resizeObserver: ResizeObserver;

  private redrawTimeout: any;
  private isDrawing: boolean = false;

  // PROPRIEDADES ESPECÍFICAS PARA RETURN LINES
  private laneWidth: number;
  private readonly colorPalette = Array.from({ length: 15 }, (_, index) => `app-flow-return-${index}`);
  private usedColors: Set<string> = new Set();
  private viewportWidth: number;

  constructor(private el: ElementRef, private ngZone: NgZone, private renderer: Renderer2) {}

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.viewportWidth = window.innerWidth;
  }

  ngAfterViewInit() {
    this.viewportWidth = window.innerWidth;
    // Só executar em telas maiores que 767px (comportamento original)
    if (this.viewportWidth <= 767) {
      return; // não executa
    }

    this.ngZone.runOutsideAngular(() => {
      this.createSVGOverlays();
      this.setupObservers();
      // Desenhar após delay para garantir que o DOM está pronto
      setTimeout(() => this.drawAllLines(), 500);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.treeSvgElement || !this.returnSvgElement) return;

    // Verificar mudanças relevantes para return lines
    if (changes['isChoosingReturn'] || changes['selectedTargetEtapa'] || changes['selectedSourceEtapa']) {
      this.scheduleRedraw();
    }
  }

  ngOnDestroy() {
    this.cleanup();
  }

  // ====================================================================
  // GERENCIAMENTO DE RECURSOS
  // ====================================================================

  // Limpa todos os recursos utilizados pela diretiva
  private cleanup() {
    if (this.redrawTimeout) {
      clearTimeout(this.redrawTimeout);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    this.removeSVGElements();
  }

  // Remove os elementos SVG do DOM
  private removeSVGElements() {
    [this.treeSvgElement, this.returnSvgElement].forEach(svg => {
      if (svg && svg.parentNode) {
        svg.parentNode.removeChild(svg);
      }
    });
  }

  // Cria os elementos SVG para ambas as funcionalidades
  // - treeSvgElement: z-index 1 (atrás)
  // - returnSvgElement: z-index 10 (frente)
  private createSVGOverlays() {
    const treeContainer = this.el.nativeElement;
    this.renderer.addClass(treeContainer, 'app-flow-container');

    // Criar SVG para tree lines (z-index menor - atrás)
    this.treeSvgElement = this.createSVG('progress-tree-lines-svg', '1');
    this.renderer.insertBefore(treeContainer, this.treeSvgElement, treeContainer.firstChild);

    // Criar SVG para return lines (z-index maior - frente)
    this.returnSvgElement = this.createSVG('progress-return-lines-svg', '10');
    this.returnDefsElement = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    this.returnSvgElement.appendChild(this.returnDefsElement);
    this.renderer.appendChild(treeContainer, this.returnSvgElement);
  }

  // Cria um elemento SVG com as configurações padrão
  private createSVG(className: string, zIndex: string): SVGElement {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.renderer.setAttribute(svg, 'class', className);
    this.renderer.addClass(svg, 'app-flow-overlay');
    this.renderer.addClass(svg, zIndex === '10' ? 'app-flow-overlay-return' : 'app-flow-overlay-tree');
    return svg;
  }

  private setupObservers() {
    // Observer de redimensionamento
    this.resizeObserver = new ResizeObserver(() => {
      if (this.viewportWidth <= 767) {
        this.removeSVGElements();
        return;
      }
      if (!this.treeSvgElement || !this.treeSvgElement.parentNode) {
        this.createSVGOverlays();
      }
      this.scheduleRedraw();
    });
    this.resizeObserver.observe(this.el.nativeElement);

    // Observer de mutações
    this.mutationObserver = new MutationObserver(mutations => {
      const relevantMutation = mutations.some(mutation => {
        const target = mutation.target as Node;

        // Ignora mudanças nos próprios SVGs
        if (this.isSVGRelated(target)) {
          return false;
        }

        // Detecta mudanças relevantes em classes
        if (mutation.type === 'attributes' && mutation.attributeName === 'class' && target instanceof Element) {
          const element = target as Element;
          const oldValue = mutation.oldValue || '';

          // Converte className para string de forma segura
          const newValue = this.getClassNameAsString(element);

          // Verifica mudança em tree-invisible (relevante para ambas as funcionalidades)
          if (oldValue.includes('tree-invisible') !== newValue.includes('tree-invisible')) {
            return true;
          }
        }

        // Detecta mudanças em data-etapa-retorno (específico para return lines)
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-etapa-retorno') {
          return true;
        }

        // Detecta adição/remoção de nós da árvore (relevante para ambas as funcionalidades)
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          return Array.from(mutation.addedNodes).some(node => {
            if (node.nodeType === 1 && node instanceof Element) {
              const classList = node.classList;
              return classList && (classList.contains('mat-tree-node') || classList.contains('mat-nested-tree-node'));
            }
            return false;
          });
        }

        return false;
      });

      if (relevantMutation) {
        this.scheduleRedraw();
      }
    });

    this.mutationObserver.observe(this.el.nativeElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'data-etapa-retorno'],
      attributeOldValue: true,
    });
  }

  // Converte className para string de forma segura
  private getClassNameAsString(element: Element): string {
    const className = element.className;

    // Verificação direta para string
    if (typeof className === 'string') {
      return className;
    }

    // Usar any para evitar problemas de inferência de tipo
    const classNameAny = className as any;

    // Verificação para DOMTokenList usando duck typing
    if (classNameAny && typeof classNameAny.toString === 'function') {
      try {
        return classNameAny.toString();
      } catch (error) {
        console.warn('Erro ao converter className para string:', error);
      }
    }

    // Fallback usando String constructor
    try {
      return String(className);
    } catch (error) {
      console.warn('Erro no fallback de conversão className:', error);
      return '';
    }
  }

  // Verifica se um nó está relacionado aos SVGs para ignorar nas mutações
  private isSVGRelated(target: Node): boolean {
    return (
      target === this.treeSvgElement ||
      target === this.returnSvgElement ||
      (this.treeSvgElement && this.treeSvgElement.contains(target)) ||
      (this.returnSvgElement && this.returnSvgElement.contains(target)) ||
      (target instanceof Element && (target.classList?.contains('progress-tree-lines-svg') || target.classList?.contains('progress-return-lines-svg')))
    );
  }

  // Agenda redesenho com debounce para evitar redesenhos excessivos
  private scheduleRedraw() {
    if (this.redrawTimeout) {
      clearTimeout(this.redrawTimeout);
    }

    // Debounce de 50ms (otimizado)
    this.redrawTimeout = setTimeout(() => {
      this.drawAllLines();
    }, 50);
  }

  // ====================================================================
  // CONTROLE PRINCIPAL DE DESENHO
  // ====================================================================

  // Método principal que desenha tanto as tree lines quanto as return lines
  private drawAllLines() {
    if (this.viewportWidth <= 767 || this.isDrawing) {
      return;
    }

    this.isDrawing = true;

    try {
      // Pausa observer durante o desenho para evitar loops
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }
      // Limpa ambos os SVGs
      this.clearSVG(this.treeSvgElement);
      this.clearSVG(this.returnSvgElement);

      // Recria defs para return lines
      this.returnDefsElement = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      this.returnSvgElement.appendChild(this.returnDefsElement);
      this.usedColors.clear();

      const treeContainer = this.el.nativeElement;

      // Atualizar tamanhos dos SVGs
      const width = `${treeContainer.scrollWidth}`;
      const height = `${treeContainer.scrollHeight}`;

      [this.treeSvgElement, this.returnSvgElement].forEach(svg => {
        this.renderer.setAttribute(svg, 'width', width);
        this.renderer.setAttribute(svg, 'height', height);
      });

      // Desenha tree lines (linhas de hierarquia)
      this.drawTreeLines();

      // Desenha return lines (linhas de retorno)
      this.drawReturnLines();
    } finally {
      this.isDrawing = false;

      // Reativa observer após o desenho
      setTimeout(() => {
        if (!this.isDrawing && this.mutationObserver) {
          this.setupObservers();
        }
      }, 100);
    }
  }

  // Limpa todos os filhos de um elemento SVG
  private clearSVG(svg: SVGElement) {
    while (svg.lastChild) {
      svg.removeChild(svg.lastChild);
    }
  }

  // ====================================================================
  // TREE LINES - LINHAS DE HIERARQUIA DA ÁRVORE
  // ====================================================================

  // Desenha todas as linhas de hierarquia da árvore
  private drawTreeLines() {
    const treeContainer = this.el.nativeElement;
    const containerRect = treeContainer.getBoundingClientRect();

    // Buscar todos os nós que têm data-etapa-id (otimizado com IDs)
    const allNodes = treeContainer.querySelectorAll('[data-etapa-id]');
    const nodesMap = new Map<string, Element>();

    allNodes.forEach(node => {
      const etapaId = (node as HTMLElement).getAttribute('data-etapa-id');
      if (etapaId) {
        nodesMap.set(etapaId, node);
      }
    });

    // Processa apenas nós expandidos com filhos visíveis
    const expandedNodes = treeContainer.querySelectorAll('.mat-nested-tree-node');

    expandedNodes.forEach((node: HTMLElement) => {
      // Verifica se o nó está expandido (tem filhos visíveis)
      const childrenContainer = node.querySelector('div[role="group"]:not(.tree-invisible)');
      if (!childrenContainer) return;

      // Busca filhos usando seletores mais específicos
      const childNodes = childrenContainer.querySelectorAll(':scope > [data-etapa-id]');
      if (childNodes.length === 0) return;

      // Obtém ID da etapa pai
      const parentEtapaId = node.getAttribute('data-etapa-id');

      // Encontra o botão/chevron do nó pai
      const parentButton = node.querySelector('button[matTreeNodeToggle]');
      if (!parentButton) return;

      const parentRect = parentButton.getBoundingClientRect();
      const parentX = parentRect.left - containerRect.left + parentRect.width / 2;
      const parentY = parentRect.top - containerRect.top + parentRect.height / 2;

      // Coleta posições apenas dos filhos diretos visíveis
      const childPositions: Array<{
        x: number;
        y: number;
        hasChildren: boolean;
        etapaId: string | null;
      }> = [];

      // Processa cada filho direto
      childNodes.forEach((childNode: Element) => {
        // Verifica se o nó filho está visível
        const isVisible = !childNode.closest('.tree-invisible');
        if (!isVisible) return;

        const childElement = childNode as HTMLElement;
        const childEtapaId = childElement.getAttribute('data-etapa-id');
        const hasChildren = childElement.classList.contains('mat-nested-tree-node');

        // Tenta encontrar o botão chevron
        let childButton = childElement.querySelector('button[matTreeNodeToggle]');

        if (!childButton) {
          // Para nós folha, usa a borda do elemento
          const nodeRect = childElement.getBoundingClientRect();
          const childX = nodeRect.left - containerRect.left;
          const childY = nodeRect.top - containerRect.top + nodeRect.height / 2;

          childPositions.push({
            x: childX,
            y: childY,
            hasChildren: false,
            etapaId: childEtapaId,
          });
        } else {
          const childRect = childButton.getBoundingClientRect();
          const childX = childRect.left - containerRect.left + childRect.width / 2;
          const childY = childRect.top - containerRect.top + childRect.height / 2;

          childPositions.push({
            x: childX,
            y: childY,
            hasChildren: hasChildren,
            etapaId: childEtapaId,
          });
        }
      });

      // Desenha linha vertical conectando apenas os filhos visíveis
      if (childPositions.length > 0) {
        const lastChildY = childPositions[childPositions.length - 1].y;

        // Linha vertical do pai até o último filho visível
        this.drawTreeVerticalLine(parentX, parentY + 20, parentX, lastChildY, parentEtapaId);

        // Desenha linhas horizontais e círculos para cada filho
        childPositions.forEach(child => {
          // Linha horizontal
          this.drawTreeHorizontalLine(parentX, child.y, child.x, child.y, parentEtapaId, child.etapaId);

          // Círculo no ponto de conexão entre linha vertical e horizontal
          this.drawTreeCircle(parentX, child.y, 'junction');

          // Se o nó tem filhos, adiciona círculo na junção com o botão
          if (child.hasChildren) {
            this.drawTreeCircle(child.x, child.y, 'node');
          }
        });
      }
    });
  }

  // [TREE LINES] Desenha uma linha vertical para tree lines
  private drawTreeVerticalLine(x1: number, y1: number, x2: number, y2: number, parentEtapaId?: string | null) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', `${x1}`);
    line.setAttribute('y1', `${y1}`);
    line.setAttribute('x2', `${x2}`);
    line.setAttribute('y2', `${y2}`);
    line.classList.add('app-flow-line');
    line.setAttribute('stroke-width', `${this.treeLineWidth}`);

    // Adiciona data attributes para identificação
    if (parentEtapaId) {
      line.setAttribute('data-parent-etapa', parentEtapaId);
    }
    line.classList.add('tree-vertical-line');

    // Aplica estilo da linha
    if (this.lineStyle === 'dashed') {
      line.setAttribute('stroke-dasharray', '5,5');
    } else if (this.lineStyle === 'dotted') {
      line.setAttribute('stroke-dasharray', '2,2');
    }

    this.treeSvgElement.appendChild(line);
  }

  // [TREE LINES] Desenha uma linha horizontal para tree lines
  private drawTreeHorizontalLine(x1: number, y1: number, x2: number, y2: number, parentEtapaId?: string | null, childEtapaId?: string | null) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', `${x1}`);
    line.setAttribute('y1', `${y1}`);
    line.setAttribute('x2', `${x2}`);
    line.setAttribute('y2', `${y2}`);
    line.classList.add('app-flow-line');
    line.setAttribute('stroke-width', `${this.treeLineWidth}`);

    // Adiciona data attributes para identificação
    if (parentEtapaId) {
      line.setAttribute('data-parent-etapa', parentEtapaId);
    }
    if (childEtapaId) {
      line.setAttribute('data-child-etapa', childEtapaId);
    }
    line.classList.add('tree-horizontal-line');

    // Aplica estilo da linha
    if (this.lineStyle === 'dashed') {
      line.setAttribute('stroke-dasharray', '5,5');
    } else if (this.lineStyle === 'dotted') {
      line.setAttribute('stroke-dasharray', '2,2');
    }

    this.treeSvgElement.appendChild(line);
  }

  // [TREE LINES] Desenha um círculo para tree lines
  private drawTreeCircle(x: number, y: number, type: 'junction' | 'node') {
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', `${x}`);
    circle.setAttribute('cy', `${y}`);
    circle.setAttribute('r', (this.treeLineWidth * 2).toString());
    circle.classList.add('app-flow-circle');
    circle.setAttribute('stroke-width', this.treeLineWidth.toString());
    circle.classList.add(`tree-circle-${type}`);

    this.treeSvgElement.appendChild(circle);
  }

  // ====================================================================
  // RETURN LINES - LINHAS DE RETORNO/SUBFLUXO
  // ====================================================================

  // [RETURN LINES] Desenha todas as linhas de retorno
  private drawReturnLines(): void {
    if (this.isChoosingReturn && this.selectedSourceEtapa && this.selectedTargetEtapa) {
      this.drawTemporaryReturnLine();
    } else if (!this.isChoosingReturn) {
      this.drawAllReturnLines();
    }
  }

  // [RETURN LINES] Desenha todas as linhas de retorno definitivas
  private drawAllReturnLines(): void {
    const connections = this.collectReturnConnections();

    if (connections.length === 0) return;

    // NOVA LÓGICA: laneWidth dinâmico baseado no espaço e quantidade de conexões
    const treeContainer = this.el.nativeElement;
    const containerWidth = treeContainer.scrollWidth;

    // Área das pistas como porcentagem da largura da viewport
    const pistaAreaWidth = Math.max(100, this.viewportWidth * 0.12);

    // Calcular laneWidth dinâmico baseado no espaço disponível
    const totalConnections = connections.length;
    const availableSpace = pistaAreaWidth * 0.8; // 80% da área para deixar margem
    this.laneWidth = Math.max(15, Math.min(40, availableSpace / totalConnections)); // Entre 15px e 40px

    const pistaAreaStart = containerWidth - pistaAreaWidth;

    // Centralizar o grupo dentro da área direita
    const totalPistasWidth = (totalConnections - 1) * this.laneWidth;
    const centralizedStartX = pistaAreaStart + (pistaAreaWidth - totalPistasWidth) / 2;

    connections.forEach((connection, index) => {
      connection.laneIndex = index;
      connection.color = this.getNextReturnColor();
      this.drawReturnLine(connection, centralizedStartX);
    });
  }

  // [RETURN LINES] Desenha linha temporária durante seleção de etapa de retorno
  private drawTemporaryReturnLine(): void {
    const treeContainer = this.el.nativeElement;
    const sourceEl = treeContainer.querySelector(`[data-etapa-id="${this.selectedSourceEtapa.CODETAPAFLUXOANDAMENTO}"]`);
    const targetEl = treeContainer.querySelector(`[data-etapa-id="${this.selectedTargetEtapa.CODETAPAFLUXOANDAMENTO}"]`);

    // Verifica se nenhuma das etapas é emprestada
    if (sourceEl && targetEl) {
      const sourceEmprestada = sourceEl.getAttribute('data-etapa-emprestada') === 'true';
      const targetEmprestada = targetEl.getAttribute('data-etapa-emprestada') === 'true';

      if (sourceEmprestada || targetEmprestada) {
        return; // Não desenha linha temporária para etapas emprestadas
      }

      // Continua com o desenho normal...
      const targetVisible = !this.isElementHidden(targetEl);
      // ... resto do código existente
    }
  }
  // private drawTemporaryReturnLine(): void {
  //   const treeContainer = this.el.nativeElement;
  //   const sourceEl = treeContainer.querySelector(`[data-etapa-id="${this.selectedSourceEtapa.CODETAPAFLUXOANDAMENTO}"]`);
  //   const targetEl = treeContainer.querySelector(`[data-etapa-id="${this.selectedTargetEtapa.CODETAPAFLUXOANDAMENTO}"]`);

  //   if (sourceEl && targetEl) {
  //     const targetVisible = !this.isElementHidden(targetEl);

  //     const connection: any = {
  //       sourceElement: sourceEl as HTMLElement,
  //       targetElement: targetEl as HTMLElement,
  //       isIndirect: !targetVisible,
  //       color: '#FF7B07',
  //       laneIndex: 0,
  //     };

  //     if (!targetVisible) {
  //       const ancestorInfo = this.findVisibleAncestor(targetEl);
  //       if (ancestorInfo) {
  //         connection.targetElement = ancestorInfo.element;
  //         connection.actualTargetId = this.selectedTargetEtapa.CODETAPAFLUXOANDAMENTO;
  //       }
  //     }

  //     // NOVA LÓGICA: laneWidth dinâmico para linha temporária também
  //     const containerWidth = treeContainer.scrollWidth;
  //     const pistaAreaWidth = Math.max(100, this.viewportWidth * 0.12);

  //     this.laneWidth = Math.max(20, Math.min(50, pistaAreaWidth * 0.3)); // Para linha única

  //     const pistaAreaStart = containerWidth - pistaAreaWidth;
  //     const temporaryStartX = pistaAreaStart + pistaAreaWidth / 2;

  //     const path = this.drawReturnLine(connection, temporaryStartX);
  //     path.classList.add('temporary-line');
  //   }
  // }

  // [RETURN LINES] Coleta todas as conexões de retorno do DOM
  private collectReturnConnections(): any[] {
    const treeContainer = this.el.nativeElement;
    const connections: any[] = [];

    const elementsWithReturn = treeContainer.querySelectorAll('[data-etapa-retorno]:not([data-etapa-retorno=""])');

    elementsWithReturn.forEach(sourceEl => {
      // Ignora se está oculto
      if (this.isElementHidden(sourceEl)) {
        return;
      }

      // IGNORA ETAPAS EMPRESTADAS (fonte)
      if (sourceEl.getAttribute('data-etapa-emprestada') === 'true') {
        return;
      }

      const sourceId = parseInt(sourceEl.getAttribute('data-etapa-id'));
      const targetId = parseInt(sourceEl.getAttribute('data-etapa-retorno'));

      if (targetId) {
        // Verifica se o target existe e não é emprestado
        const targetEl = treeContainer.querySelector(`[data-etapa-id="${targetId}"]`);
        if (targetEl && targetEl.getAttribute('data-etapa-emprestada') === 'true') {
          // IGNORA SE O ALVO É EMPRESTADO
          return;
        }

        const targetInfo = this.findReturnTargetElement(targetId);
        if (targetInfo) {
          connections.push({
            sourceEtapaId: sourceId,
            targetEtapaId: targetId,
            sourceElement: sourceEl as HTMLElement,
            targetElement: targetInfo.element,
            isIndirect: targetInfo.isIndirect,
            actualTargetId: targetInfo.actualTargetId,
          });
        }
      }
    });

    return connections;
  }
  // private collectReturnConnections(): any[] {
  //   const treeContainer = this.el.nativeElement;
  //   const connections: any[] = [];

  //   const elementsWithReturn = treeContainer.querySelectorAll('[data-etapa-retorno]:not([data-etapa-retorno=""])');

  //   elementsWithReturn.forEach(sourceEl => {
  //     if (this.isElementHidden(sourceEl)) {
  //       return;
  //     }

  //     const sourceId = parseInt(sourceEl.getAttribute('data-etapa-id'));
  //     const targetId = parseInt(sourceEl.getAttribute('data-etapa-retorno'));

  //     if (targetId) {
  //       const targetInfo = this.findReturnTargetElement(targetId);

  //       if (targetInfo) {
  //         connections.push({
  //           sourceEtapaId: sourceId,
  //           targetEtapaId: targetId,
  //           sourceElement: sourceEl as HTMLElement,
  //           targetElement: targetInfo.element,
  //           isIndirect: targetInfo.isIndirect,
  //           actualTargetId: targetInfo.actualTargetId,
  //         });
  //       }
  //     }
  //   });

  //   return connections;
  // }

  // [RETURN LINES] Encontra o elemento de destino para uma linha de retorno
  private findReturnTargetElement(targetId: number): any {
    const treeContainer = this.el.nativeElement;
    const directTarget = treeContainer.querySelector(`[data-etapa-id="${targetId}"]`);

    if (!directTarget) return null;

    if (!this.isElementHidden(directTarget)) {
      return {
        element: directTarget as HTMLElement,
        isIndirect: false,
      };
    }

    const ancestorInfo = this.findVisibleAncestor(directTarget);
    if (ancestorInfo) {
      return {
        element: ancestorInfo.element,
        isIndirect: true,
        actualTargetId: targetId,
      };
    }

    return null;
  }

  // [RETURN LINES] Encontra o ancestral visível mais próximo de um elemento
  private findVisibleAncestor(element: Element): any {
    const treeContainer = this.el.nativeElement;
    let ancestor = element.parentElement;

    while (ancestor && ancestor !== treeContainer) {
      if (ancestor.hasAttribute('data-etapa-id') && !this.isElementHidden(ancestor)) {
        return {
          element: ancestor as HTMLElement,
        };
      }
      ancestor = ancestor.parentElement;
    }

    return null;
  }

  // [RETURN LINES] Verifica se um elemento está oculto (tree-invisible)
  private isElementHidden(element: Element): boolean {
    const treeContainer = this.el.nativeElement;
    let current = element;
    while (current && current !== treeContainer) {
      if (current.classList.contains('tree-invisible')) {
        return true;
      }
      current = current.parentElement;
    }
    return false;
  }

  // [RETURN LINES] Desenha uma linha de retorno individual
  private drawReturnLine(connection: any, centralizedStartX: number): SVGPathElement {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');

    const source = this.getReturnPoint(connection.sourceElement, 'source');
    const target = this.getReturnPoint(connection.targetElement, connection.isIndirect ? 'indirect' : 'target');

    const pathData = this.calculateReturnPath(source, target, connection.laneIndex, centralizedStartX);

    path.setAttribute('d', pathData);
    path.classList.add(connection.color);
    path.setAttribute('stroke-width', this.returnLineWidth.toString());
    path.setAttribute('fill', 'none');
    path.classList.add('return-line');

    if (connection.isIndirect) {
      path.classList.add('return-line-indirect');
      path.setAttribute('stroke-dasharray', '5,3');

      const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
      title.textContent = `Retorna para subetapa oculta (ID: ${connection.actualTargetId})`;
      path.appendChild(title);
    }

    const markerId = this.createReturnArrowMarker(connection.color);
    path.setAttribute('marker-end', `url(#${markerId})`);

    if (connection.isIndirect) {
      path.classList.add('app-flow-return-interactive');
      path.addEventListener('click', () => {
        this.expandTargetEtapa.emit({
          targetId: connection.actualTargetId,
          parentId: parseInt(connection.targetElement.getAttribute('data-etapa-id')),
        });
      });
    }

    this.returnSvgElement.appendChild(path);
    return path;
  }

  // [RETURN LINES] Calcula o ponto de conexão para return lines
  private getReturnPoint(element: HTMLElement, type: 'source' | 'target' | 'indirect'): { x: number; y: number } {
    const treeContainer = this.el.nativeElement;
    const containerRect = treeContainer.getBoundingClientRect();

    // Pega o .mat-tree-node interno se for nested
    let nodeElement = element;
    if (element.classList.contains('mat-nested-tree-node')) {
      const innerNode = element.querySelector('.mat-tree-node') as HTMLElement;
      if (innerNode) {
        nodeElement = innerNode;
      }
    }

    const nodeRect = nodeElement.getBoundingClientRect();

    // Posição relativa ao container
    const x = nodeRect.right - containerRect.left;
    let y: number;

    if (type === 'indirect') {
      // Para indireto, usar a base
      y = nodeRect.bottom - containerRect.top;
    } else {
      // Para source e target direto, usar o meio
      y = nodeRect.top - containerRect.top + nodeRect.height / 2;
    }

    return { x, y };
  }

  // [RETURN LINES] Calcula o path SVG para uma linha de retorno com curvas
  private calculateReturnPath(source: { x: number; y: number }, target: { x: number; y: number }, laneIndex: number, centralizedStartX: number): string {
    // NOVA LÓGICA: Usar posição centralizada passada como parâmetro
    const laneX = centralizedStartX + laneIndex * this.laneWidth;

    const curveRadius = 6;
    const extension = 20;

    // Caminho
    let path = `M ${source.x} ${source.y}`;

    // Extensão horizontal inicial
    path += ` L ${source.x + extension} ${source.y}`;

    // Linha até antes da curva
    const approachX = laneX - curveRadius;
    if (source.x + extension < approachX) {
      path += ` L ${approachX} ${source.y}`;
    }

    // Direção da curva
    const goingDown = source.y < target.y;

    if (goingDown) {
      // Descendo - curva para BAIXO primeiro
      path += ` Q ${laneX} ${source.y} ${laneX} ${source.y + curveRadius}`;

      // Linha vertical
      if (target.y - source.y > 2 * curveRadius) {
        path += ` L ${laneX} ${target.y - curveRadius}`;
      }

      // Curva para a esquerda em direção ao target
      path += ` Q ${laneX} ${target.y} ${laneX - curveRadius} ${target.y}`;
    } else {
      // Subindo - curva para CIMA primeira
      path += ` Q ${laneX} ${source.y} ${laneX} ${source.y - curveRadius}`;

      // Linha vertical
      if (source.y - target.y > 2 * curveRadius) {
        path += ` L ${laneX} ${target.y + curveRadius}`;
      }

      // Curva para a esquerda em direção ao target
      path += ` Q ${laneX} ${target.y} ${laneX - curveRadius} ${target.y}`;
    }

    // Linha horizontal final até o target
    const finalX = target.x + this.arrowSize * 2;
    path += ` L ${finalX} ${target.y}`;

    return path;
  }

  // [RETURN LINES] Cria marcador de seta para return lines
  private createReturnArrowMarker(color: string): string {
    const markerId = `arrow-${color.replace('#', '')}-${Date.now()}`;

    const marker = document.createElementNS('http://www.w3.org/2000/svg', 'marker');
    marker.setAttribute('id', markerId);
    marker.setAttribute('markerWidth', this.arrowSize.toString());
    marker.setAttribute('markerHeight', this.arrowSize.toString());
    marker.setAttribute('refX', '0');
    marker.setAttribute('refY', (this.arrowSize / 2).toString());
    marker.setAttribute('orient', 'auto');
    marker.setAttribute('markerUnits', 'strokeWidth');

    const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    // Seta apontando para esquerda
    polygon.setAttribute('points', `0,0 ${this.arrowSize},${this.arrowSize / 2} 0,${this.arrowSize}`);
    polygon.classList.add('app-flow-arrow', color);

    marker.appendChild(polygon);
    this.returnDefsElement.appendChild(marker);

    return markerId;
  }

  // [RETURN LINES] Obtém a próxima cor disponível para return lines
  private getNextReturnColor(): string {
    for (const color of this.colorPalette) {
      if (!this.usedColors.has(color)) {
        this.usedColors.add(color);
        return color;
      }
    }
    return this.colorPalette[Math.floor(Math.random() * this.colorPalette.length)];
  }

  // ====================================================================
  // MÉTODOS PÚBLICOS - INTERFACE DA DIRETIVA
  // ====================================================================

  // Força um redesenho completo de ambas as funcionalidades
  public forceRedraw(): void {
    this.scheduleRedraw();
  }

  // Obtém um elemento por ID de etapa (compatibilidade com TreeLinesDirective)
  public getEtapaElement(etapaId: string): Element | null {
    return this.el.nativeElement.querySelector(`[data-etapa-id="${etapaId}"]`);
  }
}
